import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../ui/Toast'
import { StatusBadge } from '../ui/StatusBadge'
import { GlowCard } from '../ui/GlowCard'
import { AsBuiltExportModal } from './AsBuiltExportModal'
import { CapacityDashboard } from './CapacityDashboard'
import { Button } from '../ui/Button'
import { api } from '../../lib/api'
import { getGoogleMapsUrl } from '../../lib/mapbox'
import { useAuthStore } from '../../store/auth'
import type { ClientSite, Vlan, SiteDeviceWithSpec } from '@shared/types'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface SiteWithStats extends ClientSite {
  stats: {
    device_counts: { category: string; count: number }[]
    total_devices: number
    plan_count: number
    rack_count: number
  }
}

interface Props {
  site: SiteWithStats
  onOpenDevice: (slug: string) => void
  onNavigateDevices?: (filter?: string) => void
}

export function SiteOverview({ site, onOpenDevice, onNavigateDevices }: Props) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [devices, setDevices] = useState<SiteDeviceWithSpec[]>([])
  const [vlans, setVlans] = useState<Vlan[]>([])
  const [showExportModal, setShowExportModal] = useState(false)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showAddDevice, setShowAddDevice] = useState(false)
  const [addDeviceForm, setAddDeviceForm] = useState({ label: '', ip_address: '', status: 'planned' as const })
  const [addDeviceSaving, setAddDeviceSaving] = useState(false)

  useEffect(() => {
    api.get<{ data: SiteDeviceWithSpec[] }>(`/sites/${site.id}/devices?limit=100`)
      .then(r => setDevices(r.data))
      .catch(() => {})
    api.get<Vlan[]>(`/sites/${site.id}/vlans`)
      .then(setVlans)
      .catch(() => {})
  }, [site.id])

  const _switches = devices.filter(d => d.spec_category === 'switch')
  const _nvrs = devices.filter(d => d.spec_category === 'nvr' || d.spec_category === 'vms_server')
  const getCount = (cat: string) => {
    const entry = site.stats.device_counts.find(d => d.category === cat)
    return entry?.count ?? 0
  }

  const allStatCards = [
    { label: 'Cameras', value: getCount('camera') + getCount('surveillance'), color: 'var(--color-camera)', filter: 'camera' },
    { label: 'Doors', value: getCount('controller') + getCount('access_control'), color: 'var(--color-access)', filter: 'controller' },
    { label: 'Switches', value: getCount('switch') + getCount('networking'), color: 'var(--color-networking)', filter: 'switch' },
    { label: 'NVR/VMS', value: getCount('nvr') + getCount('vms_server'), color: 'var(--color-networking)', filter: 'nvr' },
    { label: 'Readers', value: getCount('reader'), color: 'var(--color-access)', filter: 'reader' },
    { label: 'APs', value: getCount('access_point'), color: 'var(--color-software)', filter: 'access_point' },
    { label: 'Gateways', value: getCount('gateway') + getCount('router'), color: 'var(--color-networking)', filter: 'gateway' },
    { label: 'UPS', value: getCount('ups') + getCount('power'), color: 'var(--color-power)', filter: 'ups' },
    { label: 'Servers', value: getCount('server') + getCount('infrastructure'), color: 'var(--color-infra)', filter: 'server' },
  ]
  const statCards = allStatCards.filter(s => s.value > 0)

  const address = [site.address, site.city, site.state, site.zip].filter(Boolean).join(', ')

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between" style={{ marginBottom: 24 }}>
        <div>
          {site.lat && site.lng ? (
            <a href={getGoogleMapsUrl(site.lat, site.lng)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
              <h2 className="page-title" style={{ cursor: 'pointer' }}>{address || site.name}</h2>
            </a>
          ) : (
            <h2 className="page-title">{address || site.name}</h2>
          )}
          <div className="flex items-center gap-2" style={{ marginTop: 6 }}>
            <StatusBadge status={site.status} />
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              Last updated {new Date(site.updated_at).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <div style={{ position: 'relative' }}>
            <Button variant="secondary" onClick={() => setShowExportMenu(!showExportMenu)}>
              Export
            </Button>
            {showExportMenu && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 4,
                background: 'var(--bg-surface)', borderRadius: 'var(--radius, 8px)',
                boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-light)',
                minWidth: 220, zIndex: 20, padding: '6px 0',
              }}>
                <button
                  onClick={() => { setShowExportMenu(false); setShowExportModal(true) }}
                  style={exportMenuItemStyle}
                >
                  As-Built Document (PDF)
                </button>
                <ExportCsvLink siteId={site.id} path="cable-schedule" label="Cable Schedule (CSV)" onClose={() => setShowExportMenu(false)} />
                <ExportCsvLink siteId={site.id} path="port-map" label="Port Map (CSV)" onClose={() => setShowExportMenu(false)} />
                <ExportCsvLink siteId={site.id} path="poe-budget" label="PoE Budget (CSV)" onClose={() => setShowExportMenu(false)} />
                <ExportCsvLink siteId={site.id} path="pull-list" label="Pull List (CSV)" onClose={() => setShowExportMenu(false)} />
                <ExportCsvLink siteId={site.id} path="vlan-sheet" label="VLAN Sheet (CSV)" onClose={() => setShowExportMenu(false)} />
              </div>
            )}
          </div>
          <Button onClick={() => navigate('/proposals')}>Proposals</Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="flex gap-3" style={{ marginBottom: 24 }}>
        {statCards.map((s, i) => (
          <button
            key={i}
            onClick={() => onNavigateDevices?.(s.filter)}
            className="card flex-1"
            style={{ padding: '14px 0', textAlign: 'center', cursor: 'pointer', border: '1px solid var(--border-subtle)', transition: 'all 0.15s ease' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = s.color; e.currentTarget.style.boxShadow = `0 4px 12px ${s.color}20` }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.boxShadow = 'none' }}
          >
            <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'var(--font-mono)', color: s.color, letterSpacing: '-0.02em' }}>
              {s.value}
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 }}>
              {s.label}
            </div>
          </button>
        ))}
      </div>

      {/* Capacity Dashboard — live calculations */}
      <div style={{ marginBottom: 24 }}>
        <CapacityDashboard siteId={site.id} onOpenDevice={onOpenDevice} />
      </div>

      {/* VLANs */}
      {vlans.length > 0 && (
        <GlowCard style={{ padding: 'var(--space-6)', marginBottom: 24 }} delay={0}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>VLANs</div>
          {vlans.map((v, i) => (
            <div key={v.id} className="flex items-center gap-3" style={{
              padding: '8px 0',
              borderBottom: i < vlans.length - 1 ? '1px solid var(--border-light)' : 'none',
            }}>
              <div style={{ width: 8, height: 8, borderRadius: 'var(--radius-full)', background: v.color || 'var(--text-tertiary)' }} />
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>VLAN {v.vlan_number}</span>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginLeft: 6 }}>{v.name}</span>
              </div>
              {v.subnet && (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-tertiary)' }}>{v.subnet}</span>
              )}
            </div>
          ))}
        </GlowCard>
      )}

      {/* Empty state */}
      {site.stats.total_devices === 0 && vlans.length === 0 && (
        <GlowCard style={{ textAlign: 'center', padding: '48px 24px' }} delay={100}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📡</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>No devices yet</div>
          <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 16 }}>
            Add devices to this site to start documenting your infrastructure.
          </p>
          <Button onClick={() => setShowAddDevice(true)} icon={PlusIcon}>Add Device</Button>
        </GlowCard>
      )}

      {/* Click-away to close export menu */}
      {showExportMenu && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 15 }} onClick={() => setShowExportMenu(false)} />
      )}

      {/* As-Built Export Modal */}
      <AsBuiltExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        siteId={site.id}
        siteName={site.name}
      />

      {/* Add Device Modal */}
      {showAddDevice && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setShowAddDevice(false)} />
          <div style={{ position: 'relative', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl, 12px)', padding: 24, width: 420, boxShadow: 'var(--shadow-xl)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Add Device</h3>
            <form onSubmit={async (e) => {
              e.preventDefault()
              setAddDeviceSaving(true)
              try {
                await api.post(`/sites/${site.id}/devices`, addDeviceForm)
                setShowAddDevice(false)
                setAddDeviceForm({ label: '', ip_address: '', status: 'planned' })
                window.location.reload()
              } catch { toast('Failed to add device', 'error') }
              finally { setAddDeviceSaving(false) }
            }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Label *</label>
                <input className="v3-input" required value={addDeviceForm.label} onChange={e => setAddDeviceForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Camera 1, Switch A" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>IP Address</label>
                <input className="v3-input" value={addDeviceForm.ip_address} onChange={e => setAddDeviceForm(f => ({ ...f, ip_address: e.target.value }))} placeholder="e.g. 192.168.1.100" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Status</label>
                <div className="v3-select-wrap">
                  <select className="v3-select" value={addDeviceForm.status} onChange={e => setAddDeviceForm(f => ({ ...f, status: e.target.value as typeof f.status }))}>
                    <option value="planned">Planned</option>
                    <option value="installed">Installed</option>
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                  </select>
                  <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="secondary" onClick={() => setShowAddDevice(false)}>Cancel</Button>
                <Button type="submit" loading={addDeviceSaving}>Add Device</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Export CSV helper ───

function ExportCsvLink({ siteId, path, label, onClose }: { siteId: string; path: string; label: string; onClose: () => void }) {
  const { toast } = useToast()
  const handleClick = () => {
    onClose()
    const token = useAuthStore.getState().token
    const url = `/api/sites/${siteId}/export/${path}`
    // Open download in new tab with auth
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `${path}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      })
      .catch(() => toast(`Failed to download ${label}`, 'error'))
  }

  return (
    <button onClick={handleClick} style={exportMenuItemStyle}>
      {label}
    </button>
  )
}

const exportMenuItemStyle: React.CSSProperties = {
  display: 'block', width: '100%', padding: '8px 16px',
  background: 'none', border: 'none', textAlign: 'left',
  fontSize: 13, fontWeight: 500, color: 'var(--text)',
  cursor: 'pointer',
}
